import React, { useEffect, useState } from "react";

import Reveal from "../motion/Reveal";
import { useT } from "../../i18n";
import { useTemplateVars } from "../../i18n/vars";

// Terms and Privacy. A sticky contents rail beside the document body, sections
// separated by hairlines.
//
// The rail is not decoration: Terms runs fifteen sections, and somebody
// verifying the business wants to jump to governing law without scrolling
// through the warranty clause. It tracks the reader's position as they scroll.

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [ids]);

  return active;
}

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export default function LegalDocument({ doc }) {
  const { t, tBlocks } = useT();
  const vars = useTemplateVars();
  const sections = tBlocks(`legal.${doc}.sections`, vars);
  const ids = React.useMemo(() => sections.map((s) => slugify(s.heading)), [sections]);
  const active = useActiveSection(ids);

  return (
    <section className="section-t pb-20 pt-14 lg:pb-24 lg:pt-14">
      <div className="shell flex flex-col gap-10 lg:flex-row lg:gap-20">
        <nav aria-label={t("legal.contentsLabel")} className="lg:w-[260px] lg:shrink-0">
          <div className="lg:sticky lg:top-[150px]">
            <div className="font-caption text-[10.5px] uppercase tracking-[1.6px] text-fg-muted">
              {t("legal.contentsLabel")}
            </div>
            <ul className="mt-3.5 flex flex-col">
              {sections.map((s, i) => {
                const id = ids[i];
                const on = id === active;
                return (
                  <li key={id} className="py-2">
                    <a
                      href={`#${id}`}
                      className={`font-body text-[13.5px] leading-[1.5] transition-colors duration-200
                                  ${on ? "text-accent" : "text-fg-muted hover:text-fg-secondary"}`}
                      aria-current={on ? "true" : undefined}
                    >
                      {s.heading}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        <div className="min-w-0 max-w-[700px] flex-1">
          {sections.map((s, i) => (
            <Reveal
              as="article"
              key={ids[i]}
              id={ids[i]}
              distance={10}
              className={`scroll-mt-[150px] pb-9 pt-[34px] ${i > 0 ? "border-t border-line-subtle" : "pt-0"}`}
            >
              <h2 className="font-heading text-[19px] font-semibold leading-[1.3] tracking-[-0.4px] text-fg sm:text-[21px]">
                {s.heading}
              </h2>
              <div className="mt-4 flex flex-col gap-4">
                {(Array.isArray(s.body) ? s.body : [s.body]).map((p, j) => (
                  <p key={j} className="font-body text-[16px] leading-[1.75] text-fg-body sm:text-[16.5px]">
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
